import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Loader2, ListChecks } from "lucide-react"
import type { BreakdownRow, InvoiceBreakdownLine, InvoiceBreakdownPreview } from "@/api/accounting.tsx"
import { exactCurrencyFormatter } from "@/utils"

interface InvoiceBreakdownCardProps {
  breakdown: InvoiceBreakdownPreview
  accounting_system: string
  /** The page's picks have moved and a fresh breakdown is on its way. */
  refreshing: boolean
  titleClass: string
  iconClass: string
}

function money(amount: number | string): string {
  return exactCurrencyFormatter(Number(amount))
}

/** `3662299C1 −10.93`, one per line, as the remittance numbers them. */
function RowList({ rows, empty }: { rows: BreakdownRow[]; empty: string }) {
  if (rows.length === 0) {
    return <span class="text-slate-400">{empty}</span>
  }
  return (
    <ul class="space-y-0.5">
      {rows.map((row: BreakdownRow) => (
        <li key={row.deduction_id} class="flex items-baseline gap-2 whitespace-nowrap">
          <span class="font-mono text-xs text-slate-800">{row.invoice_number}</span>
          <span class="tabular-nums text-xs text-slate-600">{money(row.amount)}</span>
        </li>
      ))}
    </ul>
  )
}

/** What the credit memo settles on the invoice, and how much of it the invoice owns. */
function CreditCell({ line }: { line: InvoiceBreakdownLine }) {
  const pooled = Number(line.pooled_credit)
  return (
    <div class="text-right">
      <div class="tabular-nums">{money(line.credit_applied)}</div>
      {pooled > 0 ? <div class="text-xs text-amber-700 whitespace-nowrap">{money(pooled)} from the pool</div> : null}
    </div>
  )
}

function DiscountCell({ line }: { line: InvoiceBreakdownLine }) {
  const discount = Number(line.early_pay_discount)
  return (
    <div class="text-right">
      <div class="tabular-nums">{money(discount)}</div>
      {discount > 0 ? (
        <div class="text-xs text-slate-500 whitespace-nowrap">{line.discount_on_payment ? "on the payment" : "on the credit memo"}</div>
      ) : null}
    </div>
  )
}

/**
 * Per invoice on the check, what the apply will post — the deductions taken against it, its
 * early-pay discount, the credit the memo lands on it and the cash the payment carries — and the
 * credit no invoice owns with where it drains. The figures are the server's own plan, so what
 * this shows is what the accounting system gets.
 */
export function InvoiceBreakdownCard({ breakdown, accounting_system, refreshing, titleClass, iconClass }: InvoiceBreakdownCardProps) {
  const pool = breakdown.pool
  const pool_total = Number(pool.total)
  const unallocated = Number(pool.unallocated)
  const has_pool = pool.rows.length > 0 || pool_total > 0
  const shows_left_open = breakdown.invoices.some((line: InvoiceBreakdownLine) => Number(line.left_open) !== 0)
  return (
    <Card class="shadow-sm border-slate-200/80">
      <CardHeader class="space-y-1 pb-4">
        <CardTitle className={titleClass}>
          <ListChecks className={iconClass} />
          What lands on each invoice
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : null}
        </CardTitle>
        <CardDescription>
          How the credit memo and the payment will settle each invoice in {accounting_system}: the credit an invoice takes is what
          the remittance took against it, the cash is what is left once that credit and the early-pay discount come off.
        </CardDescription>
      </CardHeader>
      <CardContent class="pt-0 space-y-4">
        <div class={`rounded-md border border-slate-200 overflow-hidden ${refreshing ? "opacity-60" : ""}`}>
          <div class="max-h-[min(28rem,55vh)] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                  <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3">Invoice</TableHead>
                  <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 text-right">Invoice total</TableHead>
                  <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3">Deductions taken against it</TableHead>
                  <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 text-right">Early-pay discount</TableHead>
                  {shows_left_open ? (
                    <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 text-right">Left open</TableHead>
                  ) : null}
                  <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 text-right">Credit applied</TableHead>
                  <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 text-right">Cash on the payment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {breakdown.invoices.map((line: InvoiceBreakdownLine) => (
                  <TableRow key={line.accounting_invoice_id ?? `repayment:${line.invoice_number}`}>
                    <TableCell class="px-4 py-3 align-top">
                      <div class="font-mono text-sm text-slate-900">{line.invoice_number}</div>
                      {line.accounting_invoice_id ? null : (
                        <div class="text-xs text-slate-500">Repayment invoice the apply creates</div>
                      )}
                    </TableCell>
                    <TableCell class="px-4 py-3 align-top text-right tabular-nums">{money(line.invoice_total)}</TableCell>
                    <TableCell class="px-4 py-3 align-top">
                      <RowList rows={line.deductions} empty="none" />
                    </TableCell>
                    <TableCell class="px-4 py-3 align-top">
                      <DiscountCell line={line} />
                    </TableCell>
                    {shows_left_open ? (
                      <TableCell class="px-4 py-3 align-top text-right tabular-nums">{money(line.left_open)}</TableCell>
                    ) : null}
                    <TableCell class="px-4 py-3 align-top">
                      <CreditCell line={line} />
                    </TableCell>
                    <TableCell class="px-4 py-3 align-top text-right tabular-nums font-medium text-slate-900">{money(line.cash)}</TableCell>
                  </TableRow>
                ))}
                <TableRow class="bg-slate-50/60 hover:bg-slate-50/60">
                  <TableCell class="px-4 py-3 font-medium text-slate-700" colSpan={shows_left_open ? 5 : 4}>
                    Total
                  </TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums font-medium">
                    {money(breakdown.invoices.reduce((sum: number, line: InvoiceBreakdownLine) => sum + Number(line.credit_applied), 0))}
                  </TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{money(breakdown.cash_total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

        {has_pool ? (
          <div class="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm space-y-2">
            <div class="font-medium text-slate-800">
              Credit no invoice on this check owns: {money(pool_total)}
              {pool.drains_into.length > 0 ? (
                <span class="font-normal text-slate-600">
                  {" "}
                  — applied to <span class="font-mono text-xs">{pool.drains_into.join(", ")}</span>
                  {pool.drains_into.length === 1 ? " because it comes first and has room" : " in order, as each has room"}
                </span>
              ) : null}
            </div>
            {pool.rows.length > 0 ? (
              <div class="text-slate-600">
                <div class="text-xs uppercase tracking-wide text-slate-500 mb-1">Rows that name no invoice on this check</div>
                <RowList rows={pool.rows} empty="none" />
              </div>
            ) : null}
            {unallocated > 0 ? (
              <div class="text-red-700">
                {money(unallocated)} of it fits on no invoice — the credit memo is larger than the invoices can take, so applying will be
                refused until the coding or the invoice picks change.
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
